import React from "react";
import { Link } from "react-router-dom";
import { useSiteSettings } from "../hooks/useSiteSettings";

const API_DOCS_HREF = "/api/docs";

function safeUrl(url: string): string {
  try {
    const parsed = new URL(url);
    if (parsed.protocol === "https:" || parsed.protocol === "http:") return url;
  } catch {}
  return "";
}

export default function Footer() {
  const settings = useSiteSettings();
  const rawEmail = (settings.contact_email || "info@9nd.org").trim();
  const isValidEmail = /^[^\s@\r\n]+@[^\s@\r\n]+\.[^\s@\r\n]+$/.test(rawEmail);
  const contactEmail = isValidEmail ? rawEmail : "info@9nd.org";
  const footerTagline = settings.footer_tagline || "Professional tools for modern workflows. Fast, private, and no fluff.";
  const linkedinUrl = safeUrl(settings.social_linkedin || "https://linkedin.com/company/9nd-org");
  const twitterUrl = safeUrl(settings.social_twitter || "https://twitter.com/9ndorg");
  const githubUrl = safeUrl(settings.social_github || "https://github.com/9nd-org");
  return (
    <footer className="bg-gray-950 text-gray-400">
      <div className="max-w-7xl mx-auto px-4 py-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10">

        {/* Brand */}
        <div>
          <div className="flex items-center gap-2 mb-4">
            <img
              src="/assets/brand/logo-mark.png"
              alt="9nd logo mark"
              className="w-9 h-9 object-contain"
              loading="lazy"
              decoding="async"
            />
            <span className="text-white text-lg font-extrabold tracking-tight">9nd.org</span>
          </div>
          <p className="text-sm leading-relaxed text-gray-500">{footerTagline}</p>
          <p className="mt-4 text-xs text-gray-600">No credit card required to start.</p>
          {/* Social links */}
          <div className="flex items-center gap-3 mt-5">
            <a
              href={linkedinUrl}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="LinkedIn"
              className="w-8 h-8 rounded-lg bg-gray-800 hover:bg-indigo-600 flex items-center justify-center transition text-gray-400 hover:text-white"
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
              </svg>
            </a>
            <a
              href={twitterUrl}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="X (Twitter)"
              className="w-8 h-8 rounded-lg bg-gray-800 hover:bg-gray-600 flex items-center justify-center transition text-gray-400 hover:text-white"
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.746l7.73-8.835L1.254 2.25H8.08l4.253 5.622zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
              </svg>
            </a>
            <a
              href={githubUrl}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="GitHub"
              className="w-8 h-8 rounded-lg bg-gray-800 hover:bg-gray-700 flex items-center justify-center transition text-gray-400 hover:text-white"
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z"/>
              </svg>
            </a>
          </div>
        </div>

        {/* Product */}
        <div>
          <h4 className="text-white font-semibold mb-4 text-sm uppercase tracking-widest">Product</h4>
          <ul className="space-y-3 text-sm">
            <li><Link to="/tools" className="hover:text-white transition">Tools</Link></li>
            <li><Link to="/pricing" className="hover:text-white transition">Pricing</Link></li>
            <li><Link to="/insights" className="hover:text-white transition">Insights Hub</Link></li>
            <li><Link to="/radar" className="hover:text-white transition">Technology Radar</Link></li>
            <li><Link to="/templates" className="hover:text-white transition">Templates Library</Link></li>
            <li><Link to="/tools/budget-planner" className="hover:text-white transition">Budget Planner</Link></li>
            <li><a href={API_DOCS_HREF} className="hover:text-white transition">API Docs</a></li>
          </ul>
        </div>

        {/* Company */}
        <div>
          <h4 className="text-white font-semibold mb-4 text-sm uppercase tracking-widest">Company</h4>
          <ul className="space-y-3 text-sm">
            <li><Link to="/about" className="hover:text-white transition">About Us</Link></li>
            <li><Link to="/consulting" className="hover:text-white transition">Consulting</Link></li>
            <li><Link to="/outcomes" className="hover:text-white transition">Outcomes</Link></li>
            <li><Link to="/partners" className="hover:text-white transition">Partners</Link></li>
            <li><Link to="/newsroom" className="hover:text-white transition">Newsroom</Link></li>
            <li><Link to="/playbooks" className="hover:text-white transition">Playbooks</Link></li>
            <li><Link to="/academy" className="hover:text-white transition">Learning Center</Link></li>
            <li><Link to="/trust" className="hover:text-white transition">Trust &amp; Security</Link></li>
            <li><a href={"mailto:" + contactEmail} className="hover:text-white transition">Contact</a></li>
          </ul>
        </div>

        {/* Legal + CTA */}
        <div>
          <h4 className="text-white font-semibold mb-4 text-sm uppercase tracking-widest">Legal</h4>
          <ul className="space-y-3 text-sm mb-6">
            <li><a href="/privacy" className="hover:text-white transition">Privacy Policy</a></li>
            <li><a href="/terms" className="hover:text-white transition">Terms of Service</a></li>
            <li><Link to="/trust" className="hover:text-white transition">Security</Link></li>
          </ul>
          <Link
            to="/register"
            className="inline-block bg-gradient-to-r from-indigo-600 to-purple-600 text-white text-sm font-bold px-5 py-2.5 rounded-lg hover:opacity-90 transition shadow-md"
          >
            Get Started Free →
          </Link>
        </div>
      </div>

      <div className="border-t border-gray-900 py-6 text-center text-xs text-gray-600">
        <p>&copy; {new Date().getFullYear()} 9nd.org · Built on Cloudflare Workers · Privacy-first · <Link to="/trust" className="hover:text-gray-400 transition underline underline-offset-1">Security</Link></p>
      </div>
    </footer>
  );
}

